/**
 * Production-grade logging utility for frontend application
 * Provides structured logging with different log levels and context
 */

const LOG_LEVELS = {
  ERROR: 'ERROR',
  WARN: 'WARN',
  INFO: 'INFO',
  DEBUG: 'DEBUG'
};

const LOG_COLORS = {
  ERROR: '#ff4444',
  WARN: '#ffaa00',
  INFO: '#4444ff',
  DEBUG: '#888888'
};

class Logger {
  constructor(context = 'App') {
    this.context = context;
    this.isDevelopment = import.meta.env.DEV;
    this.isProduction = import.meta.env.PROD;
  }

  /**
   * Format log message with timestamp and context
   */
  formatMessage(level, message, data = null) {
    const timestamp = new Date().toISOString();
    const logEntry = {
      timestamp,
      level,
      context: this.context,
      message,
      ...(data && { data })
    };

    return logEntry;
  }

  /**
   * Send logs to external service in production (if configured)
   */
  async sendToExternalService(logEntry) {
    if (!this.isProduction) return;

    // TODO: Integrate with logging service like Sentry, LogRocket, or custom endpoint
    // Example: await fetch('/api/logs', { method: 'POST', body: JSON.stringify(logEntry) });
  }

  /**
   * Log error with stack trace
   */
  error(message, error = null, additionalData = {}) {
    const logEntry = this.formatMessage(LOG_LEVELS.ERROR, message, {
      ...additionalData,
      ...(error && {
        errorMessage: error.message,
        errorStack: error.stack,
        errorName: error.name
      })
    });

    console.error(
      `%c[${logEntry.timestamp}] [${this.context}] ERROR:`,
      `color: ${LOG_COLORS.ERROR}; font-weight: bold`,
      message,
      logEntry.data
    );

    this.sendToExternalService(logEntry);
    return logEntry;
  }

  /**
   * Log warning
   */
  warn(message, data = null) {
    const logEntry = this.formatMessage(LOG_LEVELS.WARN, message, data);

    console.warn(
      `%c[${logEntry.timestamp}] [${this.context}] WARN:`,
      `color: ${LOG_COLORS.WARN}; font-weight: bold`,
      message,
      data
    );

    this.sendToExternalService(logEntry);
    return logEntry;
  }

  /**
   * Log informational message
   */
  info(message, data = null) {
    const logEntry = this.formatMessage(LOG_LEVELS.INFO, message, data);

    console.info(
      `%c[${logEntry.timestamp}] [${this.context}] INFO:`,
      `color: ${LOG_COLORS.INFO}`,
      message,
      data
    );

    if (this.isProduction) {
      this.sendToExternalService(logEntry);
    }
    return logEntry;
  }

  /**
   * Log debug message (only in development)
   */
  debug(message, data = null) {
    if (!this.isDevelopment) return;

    const logEntry = this.formatMessage(LOG_LEVELS.DEBUG, message, data);

    console.debug(
      `%c[${logEntry.timestamp}] [${this.context}] DEBUG:`,
      `color: ${LOG_COLORS.DEBUG}`,
      message,
      data
    );

    return logEntry;
  }

  /**
   * Start performance measurement
   */
  startTimer(label) {
    if (this.isDevelopment) {
      console.time(`[${this.context}] ${label}`);
    }
    return Date.now();
  }

  /**
   * End performance measurement
   */
  endTimer(label, startTime) {
    const duration = Date.now() - startTime;
    
    if (this.isDevelopment) {
      console.timeEnd(`[${this.context}] ${label}`);
    }

    this.debug(`${label} completed`, { duration: `${duration}ms` });
    return duration;
  }

  /**
   * Create a child logger with additional context
   */
  child(additionalContext) {
    return new Logger(`${this.context}:${additionalContext}`);
  }
}

/**
 * Create logger instance for a specific context
 */
export const createLogger = (context) => {
  return new Logger(context);
};

/**
 * Default logger instance
 */
export const logger = new Logger('App');

export default logger;
